import React, { useCallback, useEffect, useState } from 'react';
import { Users, Eye, UserPlus, BookmarkPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { api, errMsg } from '../lib/api';
import { CheerList } from './SocialBits';

function GroupMembers({ goalId, refreshKey }) {
  const [group, setGroup] = useState(null);
  const load = useCallback(() => api.get(`/groups/${goalId}`).then((r) => setGroup(r.data)).catch(() => setGroup(null)), [goalId]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const remove = async (uid) => {
    try { await api.delete(`/groups/${goalId}/members/${uid}`); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!group) return null;
  const rows = [group.owner, ...group.members];
  return (
    <div className="space-y-1.5" data-testid="group-members-list">
      {rows.map((m) => (
        <div key={m.user_id} className="flex items-center justify-between text-xs" data-testid={`group-member-${m.user_id}`}>
          <span className="text-white">{m.name} <span className="text-[#6F6981]">· {m.status === 'owner' ? 'you (owner)' : m.status}</span></span>
          <span className="flex items-center gap-2">
            <span className="font-mono text-[#C8A6FF]">${m.contributed_amount.toFixed(2)}</span>
            {m.status !== 'owner' && <button data-testid={`group-remove-${m.user_id}`} onClick={() => remove(m.user_id)} className="text-[#6F6981] hover:text-red-300 transition-colors"><X className="h-3.5 w-3.5" /></button>}
          </span>
        </div>
      ))}
    </div>
  );
}

function GroupInvite({ goalId, onInvited }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const invite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try { await api.post(`/groups/${goalId}/invite`, { email }); toast.success('Invite sent'); setEmail(''); onInvited(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="flex gap-2">
      <Input data-testid="group-invite-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Friend's FutureVault email" className="input-dark h-9 text-xs" />
      <button data-testid="group-invite-btn" disabled={busy} onClick={invite} className="shrink-0 px-3 rounded-lg bg-[#9333EA] text-white text-xs font-semibold hover:bg-[#7E22CE] transition-colors disabled:opacity-50 flex items-center gap-1"><UserPlus className="h-3.5 w-3.5" />Invite</button>
    </div>
  );
}

export function VaultSocialPanel({ goal, onUpdate }) {
  const [shared, setShared] = useState(goal.social_visibility === 'friends');
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    if (shared || goal.is_group) api.get(`/social/vaults/${goal.goal_id}`).then((r) => setDetail(r.data)).catch(() => {});
  }, [shared, goal.goal_id, goal.is_group]);

  const toggle = async (on) => {
    setShared(on);
    try { await api.patch(`/goals/${goal.goal_id}/social`, { visibility: on ? 'friends' : 'private' }); toast.success(on ? 'Friends can now follow this vault' : 'Vault is private again'); onUpdate?.(); }
    catch (err) { setShared(!on); toast.error(errMsg(err)); }
  };
  const saveTemplate = async () => {
    const days = Math.max(1, Math.round((new Date(goal.unlock_date) - new Date(goal.created_at)) / 86400000));
    try { await api.post('/templates', { name: goal.name, target_amount: goal.target_amount, duration_days: days, vault_type: goal.vault_type || 'basic' }); toast.success('Saved to your templates'); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="p-4 rounded-xl border border-white/10 bg-[#0A0A0F] space-y-4" data-testid="vault-social-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Eye className="h-4 w-4 text-[#9333EA]" /><div><p className="text-sm text-white font-semibold">Let friends follow</p><p className="text-[11px] text-[#A19DB0]">They see progress %, never dollar amounts</p></div></div>
        <Switch data-testid="vault-share-switch" checked={shared} onCheckedChange={toggle} />
      </div>
      {detail && (shared || goal.is_group) && (
        <div className="text-xs text-[#A19DB0]" data-testid="vault-follower-count">{detail.follower_count} follower{detail.follower_count === 1 ? '' : 's'} · {detail.cheers.length} cheer{detail.cheers.length === 1 ? '' : 's'}</div>
      )}
      {detail?.cheers?.length > 0 && <CheerList cheers={detail.cheers.slice(0, 3)} />}
      <div className="pt-3 border-t border-white/10 space-y-2">
        <div className="flex items-center gap-2"><Users className="h-4 w-4 text-[#9333EA]" /><p className="text-sm text-white font-semibold">{goal.is_group ? 'Group members' : 'Save together'}</p></div>
        <p className="text-[11px] text-[#A19DB0]">Invite people to add money to this vault. You hold the money and you're the only one who can withdraw.</p>
        {goal.is_group && <GroupMembers goalId={goal.goal_id} refreshKey={refreshKey} />}
        <GroupInvite goalId={goal.goal_id} onInvited={() => { setRefreshKey((k) => k + 1); onUpdate?.(); }} />
      </div>
      <button data-testid="save-as-template-btn" onClick={saveTemplate} className="flex items-center gap-1.5 text-xs text-[#A19DB0] hover:text-white transition-colors"><BookmarkPlus className="h-3.5 w-3.5" />Save as my template</button>
    </div>
  );
}
